import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Pause, ArrowDown } from "lucide-react";
import { useRadio } from "@/context/RadioContext";
import { useContent } from "@/hooks/useContent";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Hero = () => {
  const { status, toggle } = useRadio();
  const { data: site } = useContent("/content/site.json");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const playing = status === "playing" || status === "loading";

  return (
    <section id="bio" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      <div
        className="absolute inset-0 opacity-[0.14] bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1545128485-c400e7702796?q=80&w=1800&auto=format&fit=crop)" }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#08090C]/60 via-[#08090C]/80 to-[#08090C]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-12 gap-8 lg:gap-12 items-end">
        <div className="col-span-12 lg:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="font-mono text-xs uppercase tracking-[0.2em] text-amber-400/90 mb-6"
          >
            01 // Biografia &amp; Rádio 24h
          </motion.p>
          <h1 className="font-display font-extrabold uppercase tracking-tighter leading-[0.9] text-[15vw] sm:text-7xl lg:text-[4.6rem] xl:text-[5.5rem] whitespace-nowrap">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" custom={0} variants={lineReveal} initial="hidden" animate="show">
                DJ
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-amber-400" custom={1} variants={lineReveal} initial="hidden" animate="show">
                Ronaldo
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-6 text-sm sm:text-base text-zinc-300 leading-relaxed font-light max-w-xl"
          >
            {site?.bio ||
              "Apaixonado por Freestyle Music, Charme, Hip-Hop e R&B. Criador da Web Rádio Clássicos do Funk."}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-play-radio-button"
              onClick={toggle}
              className="group inline-flex items-center gap-3 bg-amber-400 text-[#08090C] font-mono text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-full hover:bg-amber-300 transition-colors duration-300"
            >
              {playing ? <Pause size={16} /> : <Play size={16} className="group-hover:scale-110 transition-transform" />}
              {playing ? "Pausar rádio" : "Ouvir rádio ao vivo"}
            </button>
            <a
              href="#contato"
              data-testid="hero-booking-cta"
              className="inline-flex items-center gap-2 border border-[#3F4462] hover:border-amber-400/70 text-zinc-200 font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full transition-colors duration-300"
            >
              Contratar show
            </a>
          </motion.div>
        </div>

        <div className="col-span-12 lg:col-span-4 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.45, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <motion.div style={{ y: imgY }} className="relative border border-[#232638] bg-[#10121A] p-3">
              <img
                src="/images/dj.png"
                alt="DJ Ronaldo"
                data-testid="hero-dj-photo"
                className="w-full aspect-[4/5] object-cover object-top"
              />
              <div className="absolute -bottom-8 -left-8 w-28 h-28 sm:w-36 sm:h-36 rounded-full vinyl-grooves border border-[#3F4462] animate-spin-slow flex items-center justify-center shadow-[0_20px_50px_rgba(0,0,0,0.7)]">
                <img src="/images/cf.jpg" alt="Clássicos do Funk" className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover" />
              </div>
            </motion.div>
            <div className="mt-14 flex items-center gap-4 border border-[#232638] bg-[#10121A]/80 backdrop-blur px-5 py-4">
              <span
                data-testid="radio-status-badge"
                className={`font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full ${
                  playing ? "bg-[#E11D48] text-white animate-live" : "bg-[#181A26] text-zinc-400"
                }`}
              >
                {status === "error" ? "Offline" : playing ? "Ao vivo" : "24h no ar"}
              </span>
              <p className="font-mono text-xs text-zinc-400 truncate">{site?.radio?.name || "Web Rádio Clássicos do Funk"}</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16 flex items-center gap-3 text-zinc-500"
      >
        <ArrowDown size={14} className="animate-bounce" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Role para explorar</span>
      </motion.div>
    </section>
  );
};

export default Hero;
